import React, { useEffect, useRef, FunctionComponent } from 'react';
import { Tabs, useTabState, usePanelState } from '../../../components/tabs';
import PaymentProviderIcons from '../components/payment_provider_icons';
import StripeCheckout from './payment_providers/stripe/stripe_checkout_form';
import PayPalButton from './payment_providers/paypal/button';
import { PaymentProvider } from './CheckoutTypes';
import '../../../../sass/apps/commerce/forms/tabbed_checkout.scss';

interface TabProps {
  ariaLabel: string;
  disabled?: boolean;
  forwardRef?: any;
  panelId: string;
}

const Tab: FunctionComponent<TabProps> = ({
  ariaLabel,
  children,
  disabled,
  forwardRef,
  panelId,
}) => {
  const { onClick, isActive } = useTabState();
  const baseClass = 'tabbed-panel__tab';
  const activeClass = isActive ? ` ${baseClass}--selected` : '';
  const disabledClass = disabled ? ` ${baseClass}--hidden` : '';
  const tabClass = baseClass + activeClass + disabledClass;

  return (
    <button
      aria-controls={panelId}
      aria-label={ariaLabel}
      aria-selected={isActive}
      className={tabClass}
      disabled={disabled}
      onClick={onClick}
      ref={forwardRef}
      role="tab"
      type="button"
    >
      {children}
    </button>
  );
};

interface PanelProps {
  id: string;
}

const Panel: FunctionComponent<PanelProps> = ({ children, id }) => {
  const isActive = usePanelState();

  return isActive ? (
    <div id={id} role="tabpanel" className="tabbed-panel__panel">
      {children}
    </div>
  ) : null;
};

export interface TabbedCheckoutProps {
  beforeSubmitErrorMessage?: () => string;
  disablePayPal?: boolean;
  disableStripe?: boolean;
  amount: string;
  paymentSuccess(provider: PaymentProvider, id: string): any;
  payPalErrorMessage?: string;
  payPalLabelText?: string;
  stripeButtonText: string;
  stripeLabelText?: string;
  staticFileServer: string;
  localisations: {
    [key: string]: string;
  };
}

export default function TabbedCheckout({
  beforeSubmitErrorMessage = () => '',
  disablePayPal = false,
  disableStripe = false,
  amount,
  paymentSuccess,
  payPalErrorMessage = '',
  payPalLabelText = 'You will be redirected to PayPal on the next step.',
  staticFileServer,
  stripeButtonText,
  stripeLabelText = 'Enter your card details',
  localisations,
}: TabbedCheckoutProps) {
  const stripeTabElement = useRef<HTMLButtonElement>(null);
  const paypalTabElement = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (disablePayPal && stripeTabElement && stripeTabElement.current) {
      stripeTabElement.current.click();
    }
  }, [disablePayPal]);
  useEffect(() => {
    if (disableStripe && paypalTabElement && paypalTabElement.current) {
      paypalTabElement.current.click();
    }
  }, [disableStripe]);
  return (
    <div className="tabbed-checkout">
      <div className="tabbed-panel">
        <Tabs state={null}>
          <div role="tablist" className="tabbed-panel__tabs">
            <Tab
              panelId="panel-1"
              ariaLabel="Pay using a card"
              forwardRef={stripeTabElement}
              disabled={disableStripe}
            >
              <PaymentProviderIcons
                className={'tabbed-checkout-icons'}
                staticFilesServer={staticFileServer}
                paymentProviders={[{ name: 'stripe' }]}
              />
            </Tab>
            <Tab
              panelId="panel-2"
              ariaLabel="Pay with PayPal"
              disabled={disablePayPal}
              forwardRef={paypalTabElement}
            >
              <img
                src="/img/spareroom/buttons/commerce/PP_logo_h_200x51.png"
                alt="PayPal Logo"
                height="25"
                width="98"
              />
            </Tab>
          </div>
          <div className="tabbed-panel__panels">
            <Panel id="panel-1">
              <div className="tabbed-checkout__stripe">
                <StripeCheckout
                  onSuccess={(id) => paymentSuccess('stripe', id)}
                  staticFilesServer={staticFileServer}
                  buttonText={stripeButtonText}
                  titleText={stripeLabelText}
                  formClass={
                    'stripe-form stripe-form--white stripe-form--no-card-icons'
                  }
                  beforeSubmitErrorMessage={beforeSubmitErrorMessage}
                  localisations={localisations}
                />
              </div>
            </Panel>
            <Panel id="panel-2">
              <article className="tabbed-checkout__paypal">
                <header className="tabbed-checkout__heading">
                  <h4 className="heading heading--sub">{payPalLabelText}</h4>
                </header>
                <main className="tabbed-checkout__body">
                  <div className="tabbed-checkout__paypal-error" role="alert">
                    {payPalErrorMessage}
                  </div>
                  <PayPalButton
                    amount={amount}
                    onSuccess={(id) => paymentSuccess('paypal', id)}
                    beforeSubmitErrorMessage={beforeSubmitErrorMessage}
                  />
                </main>
              </article>
            </Panel>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
